/* eslint-disable */
/**
 * DedupSuggestBanner
 * ==================
 * Phase-B Job Group Dedup — soft-match suggestion banner.
 *
 * Fetches ``/foh/yestimate/{id}/dedup-suggestions`` and renders a
 * "Possible duplicate?" banner per pair, each with one-tap **Merge**
 * and **Dismiss** controls. Component is intentionally self-contained
 * so the FOH pricing screen only has to mount it once.
 *
 * Renders nothing when there are no live suggestions (the engine
 * already excludes dismissed pairs server-side).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Layers, X, Link2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONFIDENCE_LABEL = {
  high:   "Strong match",
  medium: "Likely match",
  low:    "Possible match",
};

const REASON_LABEL = {
  fuzzy_title_overlap:    "Similar wording",
  shared_dtc:             "Shared diagnostic code",
  shared_system_position: "Same system & position",
  intent_overlap:         "Same diagnostic intent",
};

export const DedupSuggestBanner = ({ yestimateId, refreshKey = 0, onChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyPairId, setBusyPairId] = useState(null);

  const fetchSuggestions = useCallback(async () => {
    if (!yestimateId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/foh/yestimate/${yestimateId}/dedup-suggestions`);
      setSuggestions(res.data?.suggestions || []);
    } catch (e) {
      // Silent fail — banner is non-critical, but log so dev can spot
      // backend outages while integrating.
      // eslint-disable-next-line no-console
      console.warn("[DedupSuggestBanner] failed to fetch suggestions", e);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [yestimateId]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions, refreshKey]);

  const handleMerge = async (s) => {
    setBusyPairId(s.pair_id);
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/dedup/merge`, {
        pair_id:  s.pair_id,
        jg_a_id:  s.jg_a_id,
        jg_b_id:  s.jg_b_id,
      });
      toast.success("Merged duplicate job groups");
      // Optimistically drop the suggestion locally; parent reloads JGs.
      setSuggestions((prev) => prev.filter((p) => p.pair_id !== s.pair_id));
      if (typeof onChange === "function") await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to merge");
    } finally {
      setBusyPairId(null);
    }
  };

  const handleDismiss = async (s) => {
    setBusyPairId(s.pair_id);
    try {
      await axios.post(`${API}/foh/yestimate/${yestimateId}/dedup/dismiss`, {
        pair_id:  s.pair_id,
        jg_a_id:  s.jg_a_id,
        jg_b_id:  s.jg_b_id,
      });
      setSuggestions((prev) => prev.filter((p) => p.pair_id !== s.pair_id));
      toast("Dismissed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to dismiss");
    } finally {
      setBusyPairId(null);
    }
  };

  if (loading && suggestions.length === 0) return null;
  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50/70 shadow-sm"
      data-testid="dedup-suggest-banner"
    >
      <div className="flex items-center gap-2 px-4 py-2 border-b border-amber-200 bg-amber-100/70 rounded-t-lg">
        <Layers className="w-4 h-4 text-amber-700" />
        <span className="text-sm font-semibold text-amber-900">
          Possible duplicate{suggestions.length > 1 ? "s" : ""} detected
        </span>
        <span className="text-xs text-amber-700 ml-auto">
          {suggestions.length} suggestion{suggestions.length > 1 ? "s" : ""}
        </span>
      </div>
      <ul className="divide-y divide-amber-200">
        {suggestions.map((s) => {
          const isBusy = busyPairId === s.pair_id;
          return (
            <li
              key={s.pair_id}
              className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid={`dedup-suggest-${s.pair_id}`}
            >
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 hidden sm:block" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-amber-900">
                  <span className="font-semibold">{CONFIDENCE_LABEL[s.confidence] || "Match"}:</span>{" "}
                  <span className="italic">&ldquo;{s.jg_a_title || "(untitled)"}&rdquo;</span>
                  <span className="text-amber-700 mx-1">&amp;</span>
                  <span className="italic">&ldquo;{s.jg_b_title || "(untitled)"}&rdquo;</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(s.reasons || []).map((r) => (
                    <span
                      key={r}
                      className="text-[10px] px-1.5 py-0.5 bg-white border border-amber-300 text-amber-800 rounded uppercase tracking-wide"
                      data-testid={`dedup-reason-${r}`}
                    >
                      {REASON_LABEL[r] || r}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => handleMerge(s)}
                  className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                  data-testid={`dedup-merge-btn-${s.pair_id}`}
                >
                  <Link2 className="w-3 h-3" /> Merge
                </button>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => handleDismiss(s)}
                  className="text-xs px-3 py-1.5 bg-white border border-amber-300 text-amber-800 rounded hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1"
                  data-testid={`dedup-dismiss-btn-${s.pair_id}`}
                >
                  <X className="w-3 h-3" /> Not a duplicate
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default DedupSuggestBanner;
